// src/components/Dashboard/OverviewCards.jsx
//
// Cards del Resumen del panel de administración (solo admin: el ajustador no
// entra a la pestaña "analytics"). Toma los números de calculateAnalytics()
// (utils/analytics.js); el gráfico mensual llega ya armado como `chart` para
// que recharts siga cargándose aparte.
import React from 'react';
import './OverviewCards.css';

const monthKeyOf = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

// schedule: card de próximas citas (UpcomingMeetingsWidget) bajo los números.
const OverviewCards = ({ stats, formatCurrency, language, chart, schedule = null, onNavigate, now = new Date() }) => {
  const es = language === 'es';
  const t = (spanish, english) => (es ? spanish : english);
  const thisMonth = monthKeyOf(now);
  const lastMonth = monthKeyOf(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  const monthAmount = key => (stats.monthlyStats || []).find(item => item.monthKey === key)?.amount || 0;
  const csrThisMonth = monthAmount(thisMonth);
  const csrLastMonth = monthAmount(lastMonth);
  const delta = csrLastMonth > 0 ? (csrThisMonth - csrLastMonth) / csrLastMonth * 100 : null;
  const monthName = now.toLocaleDateString(es ? 'es-ES' : 'en-US', { month: 'long' });
  const companies = stats.companyStats || [];
  const totalCompanyCSR = companies.reduce((total, company) => total + company.csrCollected, 0);
  const completion = Math.round(stats.completionRate || 0);
  const topAdjuster = [...(stats.adjusterStats || [])].sort((a, b) => b.csrCollected - a.csrCollected)[0];
  const formatDate = value => (value
    ? new Date(`${value}T12:00:00`).toLocaleDateString(es ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : t('Sin fecha', 'No date'));

  // Medidor semicircular: el arco recorre 180° según la tasa de cierre.
  const angle = Math.PI * (1 - Math.min(Math.max(completion, 0), 100) / 100);
  const gaugeEnd = { x: 100 + 80 * Math.cos(angle), y: 100 - 80 * Math.sin(angle) };

  return (
    <div className="ov">
      <section className="ov-kpis" aria-label={t('Indicadores principales', 'Key figures')}>
        <article className="ov-card">
          <p className="ov-label">{t('CSR generado en', 'CSR generated in')} {monthName}</p>
          <p className="ov-value">{formatCurrency(csrThisMonth)}</p>
          <p className="ov-foot">
            <span>{t('vs. mes anterior', 'vs. last month')}</span>
            {delta === null
              ? <span className="ov-tag">—</span>
              : <span className={`ov-tag ${delta >= 0 ? 'is-up' : 'is-down'}`}>{delta >= 0 ? '+' : ''}{delta.toFixed(1)}%</span>}
          </p>
        </article>
        <article className="ov-card">
          <p className="ov-label">{t('Reclamos abiertos', 'Open claims')}</p>
          <p className="ov-value">{stats.pendingClaims || 0}</p>
          <p className="ov-foot"><span>{t(`${stats.completedClaims || 0} completados de ${stats.totalClaims || 0}`, `${stats.completedClaims || 0} completed of ${stats.totalClaims || 0}`)}</span></p>
        </article>
        <article className="ov-card">
          <p className="ov-label">{t('Pendiente por cobrar', 'Pending collection')}</p>
          <p className="ov-value">{formatCurrency(stats.totalPending)}</p>
          <p className="ov-foot"><span>{t('Total pagado', 'Total paid')}: {formatCurrency(stats.totalCSRPaid)}</span></p>
        </article>
        <article className="ov-card ov-card-highlight">
          <p className="ov-label">{t('Ganancia real', 'Real profit')}</p>
          <p className="ov-value">{formatCurrency(stats.realOperatorProfit)}</p>
          <p className="ov-foot"><span>{t('CSR generado − deuda de operador', 'CSR generated − operator debt')}</span></p>
        </article>
      </section>

      {schedule}

      {companies.length > 0 && (
        <section className="ov-section" aria-labelledby="ov-companies-title">
          <h3 id="ov-companies-title" className="ov-section-title">{t('Ganancia por empresa', 'Earnings by company')}</h3>
          <div className="ov-companies">
            {companies.map(company => {
              const share = totalCompanyCSR > 0 ? company.csrCollected / totalCompanyCSR * 100 : 0;
              return (
                <article key={company.name} className="ov-card ov-company">
                  <header className="ov-company-head">
                    <h4>{company.name}</h4>
                    <span className="ov-tag">{share.toFixed(0)}% {t('del total', 'of total')}</span>
                  </header>
                  <p className="ov-label">{t('CSR generado', 'CSR generated')}</p>
                  <p className="ov-value">{formatCurrency(company.csrCollected)}</p>
                  <div className="ov-bar" role="img" aria-label={`${share.toFixed(0)}% ${t('del CSR total', 'of total CSR')}`}><span style={{ width: `${share}%` }} /></div>
                  <dl className="ov-company-grid">
                    <div><dt>{t('Este mes', 'This month')}</dt><dd>{formatCurrency(company.months?.[thisMonth] || 0)}</dd></div>
                    <div><dt>{t('Por cobrar', 'Pending')}</dt><dd>{formatCurrency(company.pending)}</dd></div>
                    <div><dt>{t('Reclamos', 'Claims')}</dt><dd>{company.totalClaims}</dd></div>
                    <div><dt>{t('Completados', 'Completed')}</dt><dd>{company.completedClaims}</dd></div>
                  </dl>
                </article>
              );
            })}
          </div>
        </section>
      )}

      <section className="ov-row">
        <article className="ov-card ov-chart">
          <h3 className="ov-card-title">{t('CSR generado por mes del cheque', 'CSR generated by check month')}</h3>
          {chart}
        </article>
        <article className="ov-card ov-gauge">
          <h3 className="ov-card-title">{t('Tasa de cierre', 'Completion rate')}</h3>
          <svg viewBox="0 0 200 112" role="img" aria-label={`${completion}%`}>
            <path d="M20 100 A80 80 0 0 1 180 100" className="ov-gauge-track" />
            {completion > 0 && <path d={`M20 100 A80 80 0 0 1 ${gaugeEnd.x.toFixed(2)} ${gaugeEnd.y.toFixed(2)}`} className="ov-gauge-fill" />}
            <text x="100" y="94" textAnchor="middle">{completion}%</text>
          </svg>
          <p className="ov-foot ov-center"><span>{t(`${stats.completedClaims || 0} de ${stats.totalClaims || 0} reclamos con todos los cheques pagados`, `${stats.completedClaims || 0} of ${stats.totalClaims || 0} claims with every check paid`)}</span></p>
        </article>
      </section>

      <section className="ov-row ov-row-3">
        <article className="ov-card">
          <h3 className="ov-card-title">{t('Deuda de operador', 'Operator debt')}</h3>
          <p className="ov-value">{formatCurrency(stats.totalOperatorDebtPending)}</p>
          <p className="ov-foot"><span>{t('Pagado', 'Paid')}: {formatCurrency(stats.totalOperatorDebtPaid)} · {t('Total', 'Total')}: {formatCurrency(stats.totalOperatorDebt)}</span></p>
          <button type="button" className="ov-btn" onClick={() => onNavigate?.('debts')}>{t('Ver deuda', 'View debt')}</button>
        </article>
        <article className="ov-card ov-span-2">
          <h3 className="ov-card-title">{t('Últimos cheques recibidos', 'Latest checks received')}</h3>
          {(stats.recentChecks || []).length === 0 ? (
            <p className="ov-foot"><span>{t('Todavía no hay cheques recibidos.', 'No checks received yet.')}</span></p>
          ) : (
            <ul className="ov-list">
              {stats.recentChecks.map(check => (
                <li key={check.id}>
                  <div><strong>{check.clientName || '—'}</strong><span>{check.companyName || '—'} · {formatDate(check.checkDate)}</span></div>
                  <div className="ov-list-amount"><strong>{formatCurrency(check.csrCollected)}</strong><span>{t('de', 'of')} {formatCurrency(check.amount)}</span></div>
                </li>
              ))}
            </ul>
          )}
        </article>
        {topAdjuster && topAdjuster.csrCollected > 0 && (
          <article className="ov-card ov-profile">
            <h3 className="ov-card-title">{t('Ajustador con más CSR', 'Top adjuster by CSR')}</h3>
            <div className="ov-avatar" aria-hidden="true">{topAdjuster.name.split(/\s+/).map(part => part[0]).slice(0, 2).join('').toUpperCase()}</div>
            <p className="ov-profile-name">{topAdjuster.name}</p>
            <p className="ov-foot ov-center"><span>{topAdjuster.company || '—'}</span></p>
            <dl className="ov-profile-stats">
              <div><dd>{topAdjuster.totalClaims}</dd><dt>{t('Reclamos', 'Claims')}</dt></div>
              <div><dd>{formatCurrency(topAdjuster.csrCollected)}</dd><dt>CSR</dt></div>
            </dl>
          </article>
        )}
      </section>
    </div>
  );
};

export default OverviewCards;
